import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { getUserLocation } from "../lib/geo";
import { toast } from "sonner";
import { ArrowLeft, Eye, Mars, Venus, Circle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { avatarSrc } from "../lib/api";
import Scanning from "../components/app/Scanning";
import LocationDenied from "../components/app/LocationDenied";

function feet(m) { return m == null ? null : Math.round(m * 3.28084); }

export default function ScanNearby() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [venue, setVenue] = useState(null);
  const [scope, setScope] = useState("ambient");
  const [loading, setLoading] = useState(true);
  const [locDenied, setLocDenied] = useState(false);
  const [locReason, setLocReason] = useState("denied");
  // Minimum 2-second scanning overlay so the moment registers as a discovery action
  const [scanning, setScanning] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setScanning(false), 2200);
    return () => clearTimeout(t);
  }, []);

  const ensure = async () => {
    const me = await api.get("/presence/me");
    if (me.data.presence) return true;
    try {
      const { coords } = await getUserLocation();
      await api.post("/presence/ping", {
        lat: coords.latitude, lng: coords.longitude,
        mode: "anonymous", vibe_tag: "passive", radius_m: 150,
      });
      return true;
    } catch (r) {
      setLocReason(r?.reason || "unavailable");
      setLocDenied(true);
      return false;
    }
  };

  const retryLocation = async () => {
    setLocDenied(false);
    setLoading(true);
    setScanning(true);
    setTimeout(() => setScanning(false), 2200);
    const ok = await ensure();
    if (ok) load();
    else setLoading(false);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/scan/ambient");
      setUsers(data.users); setVenue(data.venue); setScope(data.scope);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { (async () => { const ok = await ensure(); if (ok) load(); else { setLoading(false); } })(); }, []);

  const blink = async (u) => {
    try {
      const { data } = await api.post("/interests", { to_user: u.id, venue_id: venue?.id || "__ambient__" });
      if (data.mutual) nav(`/match/${data.match.id}`);
      else toast.success(t("nearby.blinkSent",{name:u.real_name || u.display_name || t("nearby.them")}));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  // Early return: recovery UI fully replaces the page so nothing shows behind it.
  if (locDenied) {
    return <LocationDenied onRetry={retryLocation} reason={locReason} />;
  }

  return (
    <div className="min-h-screen bg-black text-white relative">
      {(loading || scanning) && <Scanning subtitle={t("nearby.lookingForNearby")} />}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(230,0,0,0.12)_0%,transparent_70%)] pointer-events-none" />
      <div className="relative max-w-md mx-auto px-5 pt-6 pb-20">
        <div className="flex justify-between items-center" data-testid="nearby-header">
          <Link to="/home" className="flex items-center gap-2 font-mono-accent text-zinc-300 hover:text-white" data-testid="nearby-back">
            <ArrowLeft size={16} className="rtl:rotate-180"/> {t("nearby.back")}
          </Link>
          <div className="flex items-center gap-2 font-mono-accent text-zinc-300">
            <Eye size={14} className="text-[#E60000]" /> {t("nearby.visible")}
          </div>
        </div>

        <h1 className="mt-5 text-5xl font-bold tracking-tight" style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 800 }}>{t("nearby.title")}</h1>
        <p className="mt-1 font-mono-accent text-zinc-500">{t("nearby.passiveNoBroadcast")}</p>

        <div className="mt-6 space-y-3" data-testid="nearby-list">
          {loading && <p className="text-zinc-500 text-sm">{t("scan.scanning")}</p>}
          {!loading && users.length === 0 && (
            <div className="rounded-3xl border border-white/5 bg-black/40 p-8 text-center" data-testid="nearby-empty">
              <p className="text-zinc-300 text-sm">{t("nearby.emptyWithin",{scope: scope === "venue" ? (venue?.name || t("nearby.thisVenue")) : t("nearby.meters150")})}</p>
              <p className="text-zinc-500 text-xs mt-2 font-mono-accent tracking-widest">{t("nearby.gpsAccuracy")}</p>
              <p className="text-zinc-500 text-xs mt-4 leading-relaxed">
                {t("nearby.emptyHint")}
              </p>
            </div>
          )}
          {users.map(u => (
            <UserRow key={u.id} u={u} onBlink={() => blink(u)} venue={venue} scope={scope} t={t} />
          ))}
        </div>

        <p className="mt-8 text-center font-mono-accent text-zinc-600 text-[0.65rem]">{t("nearby.readOnly")}</p>
      </div>
    </div>
  );
}

function UserRow({ u, onBlink, venue, scope, t }) {
  const ft = feet(u.distance_m);
  const isGhost = u.mode === "anonymous" || !u.real_name;
  const zone = scope === "venue" ? (venue?.name?.toUpperCase() || t("nearby.venue")) : t("nearby.street");
  return (
    <div
      className="rounded-3xl border border-white/5 bg-black/60 p-3 flex items-center gap-3 hover:border-[#E60000]/40 transition"
      data-testid={`nearby-row-${u.id}`}
    >
      <Avatar user={u} ghost={isGhost} />
      <div className="flex-1 min-w-0">
        <div className="text-white font-bold text-lg truncate">
          {isGhost ? (u.display_name || "Ghost") : (u.real_name || u.display_name || "someone")}
        </div>
        <div className="text-zinc-500 text-[10px] font-mono-accent tracking-wider">
          {isGhost && `${t("nearby.ghost")} · `}{zone}{ft != null && ` · ${ft} FT`}
        </div>
      </div>
      <button
        onClick={onBlink}
        data-testid={`nearby-blink-${u.id}`}
        className="px-5 py-2 rounded-full border border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white font-bold text-sm transition active:scale-[0.92]"
      >
        {t("nearby.blink")}
      </button>
    </div>
  );
}

function Avatar({ user, ghost }) {
  const src = !ghost ? avatarSrc(user) : null;
  if (src) {
    return <img src={src} alt="" className="w-12 h-12 rounded-full object-cover border border-white/10" />;
  }
  const Icon = user.gender_icon === "male" ? Mars : user.gender_icon === "female" ? Venus : Circle;
  return (
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-black to-[#1a0000] border border-white/10 flex items-center justify-center">
      <Icon size={20} className="text-zinc-400" />
    </div>
  );
}
